/**
 * The sold board's scoreboard: what came in, what it cost, what was kept.
 *
 * Jonathon, 2026-08-13: "need to improve the visibility on the master sold
 * card. I'm thinking a simple scoreboard for revenue in reflection to our
 * costs."
 *
 * The sold list is a table of jobs. It says which roofs were sold and nothing
 * whatever about whether selling them was worth doing — which is the question
 * somebody opens that screen holding. Four numbers answer it, and the fourth is
 * the one that matters: a good month at a thin margin and a quiet month at a
 * healthy one look identical under revenue alone.
 *
 * Every figure comes from `computeLadder`, the same function the estimate card
 * and the contact page use, so the board can never quietly disagree with the
 * job it is adding up.
 */

import type { LadderTotals } from "./ladder.js";
import { addCents, cents, ratio, type Cents } from "./money.js";

export interface Scoreboard {
  /** Contract dollars across every job counted. */
  revenue: Cents;
  /** Material, labour and expenses, less returns. What the work cost us. */
  cost: Cents;
  /** revenue − cost. */
  grossProfit: Cents;
  /** grossProfit / revenue. Zero when there is no revenue to divide by. */
  margin: number;
  /** Jobs behind the money. */
  jobs: number;
  /** revenue ÷ jobs. What a job on this board is typically worth. */
  averageJob: Cents;
}

export const emptyScoreboard = (): Scoreboard => ({
  revenue: cents(0),
  cost: cents(0),
  grossProfit: cents(0),
  margin: 0,
  jobs: 0,
  averageJob: cents(0),
});

/**
 * Add up a set of jobs.
 *
 * `jobs` is passed in rather than taken from `totals.length` because a job can
 * carry more than one live estimate — a customer who bought the roof and the
 * gutters rather than choosing between them — and that is one job with two
 * ladders. Counting ladders would inflate the count and deflate the average.
 *
 * The margin is taken on the totals rather than averaged across jobs, for the
 * same reason `lifetimeValue` does it: a $40k job at 30% and a $2k job at 60%
 * is not a 45% month.
 */
export function scoreboard(
  totals: readonly Pick<
    LadderTotals,
    "grandTotal" | "grossProfit" | "totalCost"
  >[],
  jobs: number,
): Scoreboard {
  const revenue = addCents(...totals.map((t) => t.grandTotal));
  const cost = addCents(...totals.map((t) => t.totalCost));
  const grossProfit = addCents(...totals.map((t) => t.grossProfit));

  return {
    revenue,
    cost,
    grossProfit,
    margin: ratio(grossProfit, revenue),
    jobs,
    averageJob: jobs > 0 ? cents(Math.round(revenue / jobs)) : cents(0),
  };
}

/**
 * Where a margin stops being a worry, on the sold board.
 *
 * The company sells at `TARGET_MARGIN` (40%). Landing a few points under that
 * across a month is ordinary; well under it is the thing this scoreboard exists
 * to make visible. A judgement about this business, not a statistic, which is
 * why it is a named constant rather than a number buried in a component.
 */
export const HEALTHY_SOLD_MARGIN = 0.3;
